import { emptyFunction } from 'utils';
import { NavigationBlock } from 'models/NavigationBlock';
import {
  createContext,
  ReactElement,
  ReactNode,
  Reducer,
  useContext,
  useEffect,
  useMemo,
  useReducer
} from 'react';
import { NavigationRoot } from 'models/NavigationRoot';
import { navigationMap } from './navigationMap';

interface NavigationControls {
  openLevel: (element: ReactElement) => void;
  closeLevel: () => void;
  clearLevels: () => void;
  navigationMap: (value: NavigationBlock | NavigationRoot) => ReactElement | null;
}

const NavigationTree = createContext<NavigationControls>({
  openLevel: emptyFunction,
  closeLevel: emptyFunction,
  clearLevels: emptyFunction,
  navigationMap
});
export const useNavigation = () => useContext(NavigationTree);

type OwnAction = { type: 'push'; element: ReactElement } | { type: 'pop' } | { type: 'clear' };
const ownReducer: Reducer<ReactElement[], OwnAction> = (state, action) => {
  switch (action.type) {
    case 'push':
      return [...state, action.element];
    case 'pop':
      return state.slice(0, -1);
    case 'clear':
      return [];
    default:
      return state;
  }
};

interface OwnProps {
  isOpen: boolean;
  children: (menuLevel: ReactElement[], controls: NavigationControls) => ReactNode;
}
export const Navigation = ({ isOpen, children }: OwnProps) => {
  const [panels, dispatch] = useReducer(ownReducer, []);
  const controls = useMemo(
    () => ({
      openLevel: (element: ReactElement) => dispatch({ type: 'push', element }),
      closeLevel: () => dispatch({ type: 'pop' }),
      clearLevels: () => dispatch({ type: 'clear' }),
      navigationMap
    }),
    []
  );
  useEffect(() => controls.clearLevels, [isOpen, controls.clearLevels]);
  return (
    <NavigationTree.Provider value={controls}>{children(panels, controls)}</NavigationTree.Provider>
  );
};
